import React from "react"
import { getDeviceDate } from "@myvp/shared/src/clock/get-device-date"
import useEventListener from "@myvp/shared/src/hooks/use-event-listener"
import { useQuery } from "@tanstack/react-query"
import Modal from "@myvp/shared/src/components/deprecated-modal"
import { css } from "@emotion/react"
import { I18nStateProvider } from "@myvp/shared/src/i18n/i18n-state-provider"
import defaultDateFnsLocale from "date-fns/locale/en-US"
import { Medium16Body } from "@myvp/shared/src/typography"
import useStorageState, {
  UseStorageStateStorage,
} from "@myvp/shared/src/hooks/use-storage-state"
import IconButton from "@myvp/shared/src/components/buttons/icon-button"
import Trash from "@myvp/shared/src/icons/trash"
import Cookies from "js-cookie"
import { addDays, addMinutes } from "date-fns"
import { sharedDevToolsStore } from "@myvp/shared/src/dev-tools/shared-dev-tools-stores"

interface Option {
  name: string
  command: (args: { branch: string }) => void
  keepOpenOnCommand?: boolean
}

const closeButtonCss = css`
  background-color: #ff4d4d;
  border: 2px solid #c4c4c4;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  cursor: pointer;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
`
const headerCss = css`
  margin: 0;
  font-family: "Courier New", Courier, monospace;
`
const modalCss = css`
  #modal-popup {
    height: unset;
    border: unset;
    background-color: #f0f0f0;
    border: 2px solid #c4c4c4;
    border-radius: 10px;
    width: 400px;
    padding: 20px;
    box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.3);
  }
`

const optionCss = css`
  background-color: #e0e0e0;
  border: 2px solid #c4c4c4;
  border-radius: 5px;
  padding: 10px;
  cursor: pointer;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  width: 100%;
  &:hover {
    background-color: papayawhip;
  }
  .highlight {
    font-weight: bold;
  }
`
const inputCss = css`
  width: 100%;
  padding: 10px;
  border: 2px solid #c4c4c4;
  border-radius: 5px;
  box-shadow: inset 2px 2px 5px #a0a0a0;
  font-family: "Courier New", Courier, monospace;
`
const cookieDomain =
  process.env.NODE_ENV === "development" ? ".local.com" : ".myveevatrials.com"

const createStorage = (expires: () => Date): UseStorageStateStorage => {
  return {
    setItem: (key: string, value: string) => {
      Cookies.set(key, value, {
        domain: cookieDomain,
        path: "/",
        sameSite: "lax",
        expires: expires(),
      })
    },
    getItem: (key: string) => {
      const item = Cookies.get(key)
      return item ?? null
    },
    removeItem: (key: string) => {
      Cookies.remove(key, {
        domain: cookieDomain,
        path: "/",
      })
    },
  }
}
export const SetLoginUrlStorage = createStorage(() => {
  /**
   * 15 minutes because that's how long the login url is valid for.
   * NOTE: Whenever the login url is exercised, it extends the expiry another 15 minutes
   */
  return addMinutes(getDeviceDate(), 15)
})
const Storage = createStorage(() => {
  /**
   * 180 days because this should never expire, and it gets extended every time the user adds an entry.
   * But we don't want it to be infinite, so it will technically go away if a PM never uses this feature.
   */
  return addDays(getDeviceDate(), 180)
})
function EnterMock(props: {
  close: () => void
  loginUrl: string
  project: Project
}) {
  const [value, setValue] = React.useState("")
  const [prevOptions, setPrevOptions] = useStorageState<string[]>(
    [],
    "prev-mock-redirects-" + props.project,
    {
      storage: Storage,
      refreshOnWindowFocus: true,
    }
  )
  const addItem = (item: string) => {
    let newPrevOptions = prevOptions.filter((i) => i !== item)
    newPrevOptions.unshift(item)
    newPrevOptions.splice(10)
    setPrevOptions(newPrevOptions)
  }
  const ref = React.useRef<HTMLInputElement | null>(null)
  React.useEffect(() => {
    ref.current?.focus()
  }, [])
  const openMock = (mock: string) => {
    try {
      if (!mock) {
        return
      }
      const url = new URL(
        "https://" + mock + ".myveevatrials.com" + props.loginUrl
      )
      props.close()
      if (mock.toLowerCase().startsWith("myvc-")) {
        addItem(mock.toLowerCase())
      }
      window.location.assign(url)
    } catch (e) {
      // oxlint-disable-next-line no-console
      console.error(e)
      // oxlint-disable-next-line no-alert
      window.alert("Looks like you entered an invalid URL")
    }
  }
  return (
    <Modal
      clearOnClickOutside
      visible={true}
      close={props.close}
      css={modalCss}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
          `}
        >
          <h2 css={headerCss}>Open PM/UX Env</h2>
          <button type="button" onClick={props.close} css={closeButtonCss}>
            X
          </button>
        </div>
        <input
          value={value}
          ref={ref}
          css={inputCss}
          placeholder="myvc-"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              openMock(value)
            }
          }}
          onChange={(e) => {
            setValue(e.target.value)
          }}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          {[...prevOptions, props.project + "-dev-ft"]
            .filter((option) => {
              return option + ".myveevatrials.com" !== window.location.hostname
            })
            .map((option, optionIndex) => {
              return (
                <div
                  key={option + optionIndex}
                  css={css`
                    display: flex;
                    gap: 8px;
                    align-items: center;
                  `}
                >
                  <button
                    type="button"
                    css={[
                      optionCss,
                      css`
                        text-align: start;
                      `,
                    ]}
                    onClick={() => {
                      openMock(option)
                    }}
                  >
                    <Medium16Body
                      css={css`
                        font-weight: 400;
                      `}
                    >
                      {option}
                    </Medium16Body>
                  </button>
                  {option.startsWith("myvc-") && (
                    <IconButton
                      type="button"
                      icon={Trash}
                      variant="tertiary"
                      iconColor="default"
                      height="small"
                      title="Delete"
                      onClick={() => {
                        setPrevOptions(prevOptions.filter((i) => i !== option))
                      }}
                    />
                  )}
                </div>
              )
            })}
        </div>
      </div>
    </Modal>
  )
}
const Tools = (props: {
  options: Option[]
  close: () => void
  branch: string
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const [search, _setSearch] = React.useState("")
  const [index, setIndex] = React.useState<number>(0)
  const setSearch = (newSearch: string) => {
    if (index !== null) {
      setIndex(0)
    }
    _setSearch(newSearch)
  }
  React.useEffect(() => {
    inputRef.current?.focus()
  }, [])
  const { filteredOptions, noMatch } = React.useMemo(() => {
    if (!search) {
      return { filteredOptions: props.options, noMatch: true }
    }

    const newOptions = props.options.filter((option) =>
      option.name.toLowerCase().includes(search.toLowerCase())
    )

    if (newOptions.length === 0) {
      return { filteredOptions: props.options, noMatch: true }
    }

    return { filteredOptions: newOptions, noMatch: false }
  }, [props.options, search])
  return (
    <Modal
      clearOnClickOutside
      visible={true}
      close={props.close}
      css={modalCss}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
          `}
        >
          <h2 css={headerCss}>Dev Tools</h2>
          <button onClick={props.close} css={closeButtonCss}>
            X
          </button>
        </div>
        <input
          ref={inputRef}
          css={inputCss}
          value={search}
          placeholder="Type a command"
          onChange={(e) => {
            setSearch(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              // Prevent cursor from going to beginning of the text field
              e.preventDefault()
              setIndex((oldIndex) => {
                if (oldIndex === null) {
                  // Start at first element
                  return 0
                }
                // Has gotten to the bottom of the list, go to the top
                if (oldIndex + 1 === filteredOptions.length) {
                  return 0
                }
                return oldIndex + 1
              })
            } else if (e.key === "ArrowUp") {
              // Prevent cursor from going to end of the text field
              e.preventDefault()
              setIndex((oldIndex) => {
                if (oldIndex === null) {
                  // Start at last element
                  return filteredOptions.length - 1
                }
                // Has gotten to the top of the list, go to the bottom
                if (oldIndex - 1 === -1) {
                  return filteredOptions.length - 1
                }
                return oldIndex - 1
              })
            } else if (e.key === "Enter") {
              const currentOption = filteredOptions[index]
              if (currentOption) {
                currentOption.command({ branch: props.branch })
                if (!currentOption.keepOpenOnCommand) {
                  props.close()
                }
              }
            }
          }}
        />
        <ul
          css={css`
            margin: 0;
            padding: 0;
            gap: 8px;
            list-style-type: none;
          `}
        >
          {filteredOptions.map((option, optionIndex) => {
            return (
              // oxlint-disable-next-line eslint-jsx-a11y/no-noninteractive-element-interactions, eslint-jsx-a11y/click-events-have-key-events
              <li
                data-active={optionIndex === index}
                data-testid={option.name}
                css={[
                  optionCss,
                  optionIndex === index
                    ? css`
                        background: cyan;
                        :hover {
                          background-color: cyan;
                        }
                      `
                    : null,
                  css`
                    margin-bottom: 8px;
                  `,
                ]}
                key={option.name}
                onClick={() => {
                  option.command({ branch: props.branch })
                  if (!option.keepOpenOnCommand) {
                    props.close()
                  }
                }}
              >
                {(() => {
                  if (noMatch) {
                    return option.name
                  }
                  const regex = new RegExp(`(${search})`, "gi")
                  const highlightedText = option.name.replace(
                    regex,
                    '<span class="highlight">$1</span>'
                  )
                  return (
                    <Medium16Body
                      css={css`
                        font-weight: 400;
                      `}
                      dangerouslySetInnerHTML={{ __html: highlightedText }}
                    />
                  )
                })()}
              </li>
            )
          })}
        </ul>
      </div>
    </Modal>
  )
}

type Project = "patients-us" | "sponsors-us" | "sites-us"
type Port = "3000" | "3005" | "3006"
type Variant = "local" | "mock" | "dev-ft" | null
const DevTools = (
  props:
    | {
        project: Project
        port: Port
        variant: Variant
        options?: Option[]
        loginUrl: string | null
      }
    | {
        options: Option[]
      }
) => {
  const [modal, setModal] = React.useState<"devtools" | "mock" | null>(null)
  const { data: git } = useQuery({
    // oxlint-disable-next-line web-rules/no-static-query-key
    queryKey: ["get-branch"],
    queryFn: async (): Promise<{ branch: string }> => {
      if (process.env.NODE_ENV === "development") {
        return (await fetch(`/devtools/get-branch`)).json()
      }
      return { branch: "mock" }
    },
    enabled: modal === "devtools",
  })

  const deployedMockBranch = (() => {
    if (!git?.branch) {
      return null
    }
    const match = git.branch.match(/myvc-[0-9]+/i)
    if (match) {
      return match[0]
    } else {
      return git.branch
    }
  })()

  useEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "k") {
      if (e.metaKey || e.altKey) {
        setModal("devtools")
        // avoid k being entered into the text field
        e.preventDefault()
      }
      if ("loginUrl" in props && props.loginUrl && e.ctrlKey) {
        // prevent windows from starting a google search
        e.preventDefault()
        setModal("mock")
      }
    }
  })

  let options: Option[] = props.options ?? []
  const windowSearch = new URLSearchParams(window.location.search)

  if ("project" in props && "port" in props) {
    options = options.concat([
      {
        name: "Open mock.html",
        command() {
          window.open("/mocks/mock.html")
        },
      },
      ...(props.loginUrl
        ? [
            {
              name: "Open PM/UX Env",
              command: () => {
                setModal("mock")
              },
              keepOpenOnCommand: true,
            },
          ]
        : []),
      ...(props.variant === "local" && git?.branch !== "develop"
        ? [
            {
              name: "Open Mock mock.html",
              command() {
                window.open(
                  `https://${deployedMockBranch}.myveevatrials.com/mocks/mock.html`,
                  "_blank"
                )
              },
            },
            {
              name: "Open Jira",
              command() {
                window.open(
                  `https://jira.veevadev.com/browse/${git?.branch}`,
                  "_blank"
                )
              },
            },
            {
              name: "Open Mock",
              command() {
                window.open(
                  `https://${
                    deployedMockBranch
                  }.myveevatrials.com${window.location.pathname}${
                    window.location.search
                  }`,
                  "_blank"
                )
              },
            },
            {
              name: "Open in Network",
              async command() {
                const result = await fetch(`/devtools/get-ip`)
                const { ip } = await result.json()
                window.open(
                  `http://${ip}:${props.port}${window.location.pathname}${window.location.search}`,
                  "_blank"
                )
              },
            },
          ]
        : []),
      ...(props.variant !== "local"
        ? [
            {
              name: "Open Local",
              command() {
                window.open(
                  `https://${
                    props.project
                  }.local.com:${props.port}${window.location.pathname}${
                    window.location.search
                  }`,
                  "_blank"
                )
              },
            },
          ]
        : []),
      ...(props.variant === "local"
        ? [
            {
              name: "Clear AC cookie",
              command: async () => {
                await fetch("/devtools/clear-ac-cookie", { method: "POST" })
              },
            },
          ]
        : []),
      ...(props.variant !== "dev-ft"
        ? [
            {
              name: "Open Develop",
              command() {
                window.open(
                  `https://${props.project}-dev-ft.myveevatrials.com${window.location.pathname}${window.location.search}`,
                  "_blank"
                )
              },
            },
          ]
        : []),
      windowSearch.get("skipValidation") || windowSearch.get("skipvalidation")
        ? {
            name: "Remove skipValidation",
            command() {
              const newSearchParams = new URLSearchParams(windowSearch)
              newSearchParams.delete("skipValidation")
              newSearchParams.delete("skipvalidation")
              const newURL = new URL(
                `${window.location.origin}${window.location.pathname}`
              )
              newURL.search = newSearchParams.toString()
              window.location.replace(newURL.toString())
            },
          }
        : {
            name: "Add skipValidation",
            command() {
              const newSearchParams = new URLSearchParams(windowSearch)
              newSearchParams.set("skipValidation", "true")
              const newURL = new URL(
                `${window.location.origin}${window.location.pathname}`
              )
              newURL.search = newSearchParams.toString()
              window.location.replace(newURL.toString())
            },
          },
      ...(props.variant === "local"
        ? [
            {
              name: `Toggle React Query DevTools`,
              command: () => {
                sharedDevToolsStore.setState((devtool) =>
                  devtool === "react-query" ? null : "react-query"
                )
              },
            },
            {
              name: `Toggle TanStack DevTools`,
              command: () => {
                sharedDevToolsStore.setState((devtool) =>
                  devtool === "tanstack" ? null : "tanstack"
                )
              },
            },
          ]
        : []),
      {
        name: "Open Slow Train",
        command() {
          window.open(
            `https://${props.project}-qa-st1.myveevatrials.com${window.location.pathname}${window.location.search}`,
            "_blank"
          )
        },
      },
      {
        name: "Open Fast Train",
        command() {
          window.open(
            `https://${props.project}-qa-ft2.myveevatrials.com${window.location.pathname}${window.location.search}`,
            "_blank"
          )
        },
      },
    ])
  }
  return (
    <I18nStateProvider
      dateFnsLocale={defaultDateFnsLocale}
      locale="en-US"
      updateIntl={() => {}}
      messages={{}}
      rtl={false}
    >
      {"loginUrl" in props && props.loginUrl && modal === "mock" && (
        <EnterMock
          close={() => setModal(null)}
          loginUrl={props.loginUrl}
          project={props.project}
        />
      )}
      {modal === "devtools" && git?.branch && (
        <Tools
          options={options}
          close={() => setModal(null)}
          branch={git.branch}
        />
      )}
    </I18nStateProvider>
  )
}

export default function ExportedDevTools(
  props:
    | { options: Option[] }
    | {
        project: Project
        port: Port
        options?: Option[]
        loginUrl: string | null
      }
) {
  if ("options" in props && !("project" in props) && !("port" in props)) {
    return <DevTools options={props.options} />
  }
  if (!("project" in props) || !("port" in props)) {
    return null
  }
  const variant = (() => {
    if (process.env.NODE_ENV === "development") {
      return "local"
    }
    if (window.location.hostname.startsWith("myvc-")) {
      return "mock"
    }
    if (window.location.host.startsWith(`${props.project}-dev-ft`)) {
      return "dev-ft"
    }

    return null
  })()
  return (
    <DevTools
      variant={variant}
      project={props.project}
      port={props.port}
      options={props.options}
      loginUrl={props.loginUrl}
    />
  )
}
